import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { BaseComponentProps } from "../types";
import { alpha } from "../../typography";
import { Icon, IconName, SmallPlainIcon } from "../Icon";
import React, { useEffect, useRef, useState, KeyboardEvent } from "react";
import { LabelText, Asterisk, Caption } from "../common";
import { Tooltip } from "../Tooltip";

type StyledTextFieldContainerProps = {
  disabled?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
  styles?: FlattenSimpleInterpolation;
};

const StyledTextFieldContainer = styled.div<StyledTextFieldContainerProps>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  height: 32px;
  background: var(--color-neutral-000-full);
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  :focus-within {
    outline: none;
  }
  ${(props) => {
    if (props.hasError) {
      return css`
        border: 1px solid var(--color-error-500);
      `;
    }

    if (props.readOnly) {
      return css`
        border: none;
        input {
          pointer-events: none;
        }
        input::placeholder {
          color: var(--color-primary-200);
        }
      `;
    }

    if (props.disabled) {
      return css`
        border: 1px solid var(--color-primary-200);
        input::placeholder {
          color: var(--color-primary-200);
        }
      `;
    }

    return css`
      &:hover {
        border: 1px solid var(--color-primary-800);
      }
      &:focus-within {
        border: 1px solid var(--color-secondary-800);
      }
    `;
  }}
  ${(props) => `${props.styles}`}
`;

const StyledTextField = styled.input<TextFieldProps>`
  &&& {
    display: block;
    min-width: 0px;
    flex-grow: 1;
    padding: 0px 12px;
    padding-right: 34px;
    background: inherit;
    border: none;
    border-radius: var(--size-border-radius-small);
    font-size: var(--size-font-small);
    ${(props) =>
      props.disabled
        ? alpha.primaryDisabled.body3
        : props.value
        ? alpha.primaryActive.body3
        : alpha.primaryInactive.body3};
    :focus {
      outline: none;
      ${(props) => (!props?.readOnly ? alpha.primaryActive.body3 : "")}
    }

    ::placeholder {
      ${alpha.primaryInactive.body3};
    }

    ::selection {
      ${alpha.primaryActive.body3}
      background-color: var(--color-secondary-200);
    }
  }
`;

const ReadOnlyText = styled.span<TextFieldProps>`
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0px 12px;
  padding-right: 34px;
  background: inherit;
  ${alpha.primaryInactive.body3}
`;

const StyledIconContainer = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  ${(props) =>
    props.hasError &&
    css`
      svg {
        --main-color: var(--color-error-500);
      }
    `}
`;

export type TextFieldProps = BaseComponentProps<"input"> & {
  caption?: string;
  errorMessage?: string;
  iconName?: IconName;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  styles?: FlattenSimpleInterpolation;

  /** Function called when the icon is clicked */
  iconOnClick?: (event: React.MouseEvent<HTMLElement>) => void;

  /** Function called when the input looses focus */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  /** Function called when the input value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const TextFieldComponent = ({
  caption,
  errorMessage,
  iconName,
  iconOnClick,
  onBlur,
  onChange,
  ...inputProps
}: TextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focused, inputRef]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.code === "Space" && event.currentTarget.value.length === 0) {
      event.preventDefault();
    }
  };

  return (
    <>
      <StyledTextFieldContainer
        disabled={inputProps.disabled}
        readOnly={inputProps.readOnly}
        hasError={!!errorMessage}
        styles={inputProps.styles}
      >
        {inputProps?.readOnly && (
          <ReadOnlyText>{inputProps.defaultValue}</ReadOnlyText>
        )}
        {!inputProps?.readOnly && (
          <StyledTextField
            ref={inputRef}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              onBlur && onBlur(event);
              setFocused(false);
            }}
            onKeyDown={
              inputProps.onKeyDown ? inputProps.onKeyDown : handleKeyDown
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onChange && onChange(event);
            }}
            onFocus={() => setFocused(true)}
            {...inputProps}
          />
        )}

        {iconName && (
          <StyledIconContainer hasError={!!errorMessage} onClick={iconOnClick}>
            <SmallPlainIcon
              iconName={errorMessage ? "exclamation" : iconName}
              variant={
                inputProps.disabled || inputProps.readOnly
                  ? "DISABLED"
                  : focused
                  ? "ACTIVE"
                  : "DEFAULT"
              }
            />
          </StyledIconContainer>
        )}
      </StyledTextFieldContainer>

      {/* caption shows inside a tooltip when text field is read only */}
      {((caption && !inputProps?.readOnly) || errorMessage) && (
        <Caption
          isDisabled={inputProps.disabled || inputProps.readOnly}
          hasError={!!errorMessage}
        >
          {errorMessage || caption}
        </Caption>
      )}
    </>
  );
};

export const TextField = (props: TextFieldProps) => {
  const { label, name, disabled, errorMessage, isRequired } = props;

  if (label) {
    return (
      <label htmlFor={name}>
        <LabelText
          isDisabled={disabled}
          hasError={!!errorMessage}
          isReadOnly={props.readOnly}
        >
          <span>{label}</span> {isRequired ? <Asterisk>*</Asterisk> : null}{" "}
          {props?.readOnly && props?.caption && (
            <Tooltip content={props.caption} defaultPlacement="right">
              <Icon iconSize="small" variant="ACTIVE" iconName="information" />
            </Tooltip>
          )}
        </LabelText>
        <TextFieldComponent aria-label={label} {...props} />
      </label>
    );
  }
  return <TextFieldComponent {...props} />;
};
